import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardSummary } from "./DashboardService";
import { useApiCall } from "../useApiCall";
import "./DashboardPage.css";

const todayStr = new Date().toISOString().slice(0, 10);

const QUICK_LINKS = [
  { label: "Today",    path: "/today",    icon: "☀️" },
  { label: "Planner",  path: "/planner",  icon: "📋" },
  { label: "Notes",    path: "/notes",    icon: "📝" },
  { label: "Journal",  path: "/journal",  icon: "📓" },
  { label: "Tasks",    path: "/tasks",    icon: "✅" },
  { label: "Habits",   path: "/habits",   icon: "🔄" },
  { label: "Finance",  path: "/finance",  icon: "💰" },
  { label: "Calendar", path: "/calendar", icon: "📅" },
  { label: "Files",    path: "/files",    icon: "📁" },
  { label: "Review",   path: "/review",   icon: "📊" },
];

export default function DashboardPage() {
  const nav = useNavigate();
  const { execute, loading, error } = useApiCall();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    execute(getDashboardSummary).then(d => { if (d) setSummary(d); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="db-state db-loading">Loading dashboard…</div>;
  if (error)   return <div className="db-state db-error">{error}</div>;
  if (!summary) return null;

  const { stats, today, unread, tasks, habits } = summary;

  const overdueCount  = stats.overdueCount  ?? 0;
  const todayCount    = stats.todayCount    ?? 0;
  const thisWeekCount = stats.thisWeekCount ?? 0;
  const upcomingPay   = stats.upcomingPaymentsCount ?? 0;

  const openTasks   = tasks.filter(t => t.status !== "DONE" && t.status !== "CANCELLED").length;
  const habitsLeft  = habits.filter(h => h.active && !h.completedToday).length;
  const habitsDone  = habits.filter(h => h.active && h.completedToday).length;
  const totalHabits = habits.filter(h => h.active).length;

  const urgentItems  = today?.urgent || [];
  const overdueItems = urgentItems.filter(i => i.dueDate && i.dueDate < todayStr);
  const todayItems   = urgentItems.filter(i => i.dueDate === todayStr);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const habitsPercent = totalHabits > 0 ? Math.round((habitsDone / totalHabits) * 100) : 0;
  const unreadNum = typeof unread === "number" ? unread : 0;

  return (
    <div className="db-page">
      {/* HERO */}
      <div className="db-hero">
        <p className="db-date">
          {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </p>
        <h1 className="db-greeting">{greeting}</h1>
        <p className="db-subline">
          {overdueCount > 0
            ? `You have ${overdueCount} overdue item${overdueCount !== 1 ? "s" : ""}. Let's get caught up.`
            : todayCount > 0
            ? `${todayCount} item${todayCount !== 1 ? "s" : ""} due today. You're on track.`
            : "You're all clear for today. Great work."}
        </p>
      </div>

      {/* STAT GRID */}
      <div className="db-stat-grid">
        <StatCard
          value={overdueCount} label="Overdue"
          accent={overdueCount > 0 ? "red" : "green"}
          onClick={() => nav("/planner")}
        />
        <StatCard
          value={todayCount} label="Due today"
          accent="blue"
          onClick={() => nav("/today")}
        />
        <StatCard
          value={thisWeekCount} label="This week"
          accent="indigo"
          onClick={() => nav("/planner")}
        />
        <StatCard
          value={upcomingPay} label="Payments"
          accent={upcomingPay > 0 ? "orange" : "green"}
          onClick={() => nav("/planner")}
        />
        <StatCard
          value={openTasks} label="Open tasks"
          accent="purple"
          onClick={() => nav("/tasks")}
        />
        <StatCard
          value={unreadNum} label="Unread"
          accent={unreadNum > 0 ? "orange" : "green"}
        />
        {totalHabits > 0 && (
          <div
            className={`db-stat db-stat-${habitsLeft > 0 ? "orange" : "green"} db-stat-link`}
            onClick={() => nav("/habits")}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === "Enter" && nav("/habits")}
          >
            <div className="db-stat-label">Habits today</div>
            <div className="db-stat-value">
              {habitsDone}
              <span className="db-stat-denom">/{totalHabits}</span>
            </div>
            <div className="db-stat-bar">
              <div className="db-stat-bar-fill" style={{ width: `${habitsPercent}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* OVERDUE */}
      {overdueItems.length > 0 && (
        <Section title="Overdue" onAction={() => nav("/planner")} actionLabel="View all">
          {overdueItems.slice(0, 6).map(i => <ItemRow key={i.id} item={i} overdue />)}
        </Section>
      )}

      {/* TODAY */}
      {todayItems.length > 0 && (
        <Section title="Due today">
          {todayItems.slice(0, 5).map(i => <ItemRow key={i.id} item={i} />)}
        </Section>
      )}

      {/* QUICK ACCESS */}
      <Section title="Quick access">
        <div className="db-quick-grid">
          {QUICK_LINKS.map(({ label, path, icon }) => (
            <button key={path} className="db-quick-btn" onClick={() => nav(path)}>
              <span className="db-quick-icon">{icon}</span>
              <span className="db-quick-label">{label}</span>
            </button>
          ))}
        </div>
      </Section>
    </div>
  );
}

function StatCard({ value, label, accent = "blue", onClick }) {
  return (
    <div
      className={`db-stat db-stat-${accent}${onClick ? " db-stat-link" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? e => e.key === "Enter" && onClick() : undefined}
    >
      <div className="db-stat-label">{label}</div>
      <div className="db-stat-value">{value}</div>
    </div>
  );
}

function Section({ title, children, onAction, actionLabel }) {
  return (
    <section className="db-section">
      <div className="db-section-header">
        <h3 className="db-section-title">{title}</h3>
        {onAction && (
          <button className="db-section-action" onClick={onAction}>{actionLabel} →</button>
        )}
      </div>
      <div className="db-section-body">{children}</div>
    </section>
  );
}

function ItemRow({ item, overdue }) {
  const PRIORITY = ["", "Critical", "High", "Medium", "Low", "Someday"];
  const p = typeof item.priority === "number" ? item.priority : 3;
  const priLabel = PRIORITY[p] || "";
  return (
    <div className={`db-item${overdue ? " db-item-overdue" : ""}`}>
      <div className="db-item-dot" />
      <div className="db-item-body">
        <span className="db-item-title">{item.title}</span>
        {item.category && <span className="db-item-cat">{item.category}</span>}
      </div>
      <div className="db-item-meta">
        {item.dueDate && <span className="db-item-date">{item.dueDate}</span>}
        {priLabel && (
          <span className={`db-item-pri db-pri-${priLabel.toLowerCase()}`}>{priLabel}</span>
        )}
      </div>
    </div>
  );
}
